import { ReloadOutlined } from '@ant-design/icons';
import type { LedgerRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Space, Table, Tag, Typography, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { hq } from '../api/client';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

/** `wl_stock_ledger.biz_type` 固定枚举（docs/db/wz-biz/11-wl-inventory.sql）。 */
const BIZ_TYPE_META: Record<string, string> = {
  INBOUND: '入库',
  OUTBOUND: '出库',
  TRANSFER: '调拨',
  CHECK: '盘点',
  ADJUST: '调整',
  COST_TRANSFER: '成本转移',
  RETURN: '退货'
};

/** 库存流水：只读 wl_stock_ledger，按物资过滤。 */
export default function StockLedger() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [materialId, setMaterialId] = useState(searchParams.get('materialId') ?? '');
  const [bizType, setBizType] = useState('');

  const materials = useQuery({
    queryKey: ['material', 'options'],
    queryFn: () => hq.material.list({ size: 500 }),
    staleTime: 10 * 60 * 1000
  });

  const query = useInfiniteQuery({
    queryKey: ['stock', 'ledger', materialId, bizType],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.stock.ledger({
        lastId: pageParam,
        size: PAGE_SIZE,
        materialId: materialId || undefined,
        bizType: bizType || undefined
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<LedgerRow> = [
    { title: '发生时间', dataIndex: 'occurAt', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '业务类型', dataIndex: 'bizType', width: 120, render: (value: string | null) => value || '-' },
    { title: '业务单号', dataIndex: 'bizNo', width: 200, render: (value: string | null) => value || '-' },
    {
      title: '方向',
      dataIndex: 'direction',
      width: 90,
      render: (value: number) =>
        value > 0 ? <Tag color="green">入库</Tag> : value < 0 ? <Tag color="red">出库</Tag> : <Tag>-</Tag>
    },
    { title: '数量', dataIndex: 'qty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    { title: '变动前', dataIndex: 'beforeQty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    { title: '变动后', dataIndex: 'afterQty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'amount', width: 120, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '批次', dataIndex: 'batchNo', width: 120, render: (value: string | null) => value || '-' },
    { title: '操作人', dataIndex: 'operatorName', width: 110, render: (value: string | null) => value || '-' }
  ];

  return (
    <Card
      className="page-card"
      title="库存流水"
      extra={
        <Space>
          <Button onClick={() => navigate('/stock')}>返回库存</Button>
          <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
            刷新
          </Button>
        </Space>
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        {/* 物资改选择器：手填内部雪花 ID 既记不住也查不到 */}
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="物资（名称 / 编码）"
          value={materialId || undefined}
          onChange={(value?: string) => setMaterialId(value ?? '')}
          loading={materials.isPending}
          options={(materials.data?.items ?? []).map((row) => ({
            value: row.id,
            label: `${row.materialName}（${row.materialCode}）`
          }))}
          style={{ width: 280 }}
        />
        {/* 业务类型是固定枚举（wl_stock_ledger.biz_type），改成下拉避免拼错查空 */}
        <Select
          allowClear
          placeholder="业务类型"
          value={bizType || undefined}
          onChange={(value?: string) => setBizType(value ?? '')}
          options={Object.entries(BIZ_TYPE_META).map(([value, label]) => ({ value, label }))}
          style={{ width: 180 }}
        />
        <Typography.Text type="secondary">后端游标分页；物资/类型留空表示不过滤</Typography.Text>
      </Space>

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="流水加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<LedgerRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无流水' }}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : rows.length > 0 ? (
          <Typography.Text type="secondary">没有更多了</Typography.Text>
        ) : null}
      </Space>
    </Card>
  );
}
